/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\IntegrationsMarketplace.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Integrations Config module.
----------------------------------------------------------------*/

import React from 'react';

import { useNavigate } from 'react-router-dom';
import {
    Cloud,
    Briefcase,
    Activity,
    Hexagon,
    Package,
    Globe,
    CheckCircle2,
    ArrowRight
} from 'lucide-react';
import { ModuleFormHeader } from '../../../components/layouts';
import { useI18n } from '../../../hooks/useI18n';

export const IntegrationsMarketplace = () => {
    const { t } = useI18n();
    const navigate = useNavigate();

    const crms = [
        { id: 'hubspot', name: t('integrations.providers.hubspot.name'), icon: Hexagon, color: 'text-orange-500', description: t('integrations.providers.hubspot.description') },
        { id: 'salesforce', name: t('integrations.providers.salesforce.name'), icon: Cloud, color: 'text-blue-500', description: t('integrations.providers.salesforce.description') },
        { id: 'zoho', name: t('integrations.providers.zoho.name'), icon: Briefcase, color: 'text-yellow-600', description: t('integrations.providers.zoho.description') },
        { id: 'pipedrive', name: t('integrations.providers.pipedrive.name'), icon: Activity, color: 'text-green-500', description: t('integrations.providers.pipedrive.description') },
        { id: 'dynamics', name: t('integrations.providers.dynamics.name'), icon: Package, color: 'text-blue-700', description: t('integrations.providers.dynamics.description') },
        { id: 'odoo', name: t('integrations.providers.odoo.name'), icon: Globe, color: 'text-purple-600', description: t('integrations.providers.odoo.description') },
        { id: 'calendly', name: t('integrations.providers.calendly.name'), icon: CheckCircle2, color: 'text-cyan-500', description: t('integrations.providers.calendly.description') },
    ];

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Cloud className="h-5 w-5 text-primary" />}
                title={t('integrations.title')}
                backButtonText={t('common.exit')}
                onBack={() => navigate('/dashboard')}
                actions={[]}
            />

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {crms.map((crm) => (
                    <div
                        key={crm.id}
                        className="group relative bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200 hover:border-primary/50 flex flex-col h-full"
                    >
                        <div className="flex items-center gap-4 mb-3">
                            <div className={`p-2.5 rounded-lg bg-background border border-border group-hover:bg-primary/5 transition-colors ${crm.color}`}>
                                <crm.icon className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-semibold text-foreground">{crm.name}</h3>
                        </div>

                        <p className="text-sm text-muted-foreground flex-grow mb-4 line-clamp-2">
                            {crm.description}
                        </p>

                        <div className="flex items-center justify-end mt-auto pt-3 border-t border-border/50">
                            <button
                                onClick={() => navigate(`/settings/integrations/${crm.id}`)}
                                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-md transition-colors w-full sm:w-auto"
                            >
                                {t('integrations.configure')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
